import { StackScreenProps } from "@react-navigation/stack";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { useCartController } from "@/contexts/CartControllerContext";
import { useViewport } from "@/contexts/ViewportContext";
import { useAppNavigation } from "@/hooks/application/navigation";
import { useAppSelector } from "@/hooks/application/store";
import {
  useAddFavorite,
  useClientFavoriteProducts,
  useRemoveFavorite,
} from "@/hooks/domain/favoriteProducts";
import { useProductById } from "@/hooks/domain/product";
import { OrderItemQualityEnum } from "@/interfaces/domain/Order";
import { Store } from "@/interfaces/domain/Store";
import { HomeNavigation, HomeStackParamList } from "@/navigation/stacks/HomeStack";

import ProductMobile from "./Product.mobile";
import ProductWeb from "./Product.web";

type ScreenProps = StackScreenProps<HomeStackParamList, "product">;

const initialVariables = {
  quantity: 0,
  total: 0,
  instruction: "",
  quality: null,
};

const Product = ({ route }: ScreenProps) => {
  const productId = route?.params?.productId;

  const { goBack, setOptions } = useAppNavigation<HomeNavigation>();
  const { isWebMode } = useViewport();
  const { decreaseItem, increaseItem, changeItemInstruction, changeItemQuality } =
    useCartController();

  const items = useAppSelector((store) => store.checkout.items);
  const [variables, setVariables] = useState(initialVariables);
  const [isFavorite, setFavorite] = useState(false);

  const { data: product, isLoading } = useProductById(productId, {
    onError: () => {
      goBack();
    },
    onSuccess: (res) => {
      if (res) {
        setOptions({ title: res?.nome });
        const existentItem = items.get(res?._id);
        if (existentItem) {
          setVariables({
            instruction: existentItem.instrucao,
            quality: existentItem.qualidade,
            quantity: existentItem.quantidade,
            total: existentItem.preco_total,
          });
        }
      }
    },
  });

  const storeId = (product?.item?.estabelecimento as Store)?._id;

  const { checkFavoriteByProductId } = useClientFavoriteProducts(storeId, {
    enabled: !!storeId,
  });

  const { mutate: addFavorite } = useAddFavorite(storeId, {
    onSuccess: () => {
      setFavorite((prev) => !prev);
    },
  });
  const { mutate: removeFavorite } = useRemoveFavorite(storeId, {
    onSuccess: () => {
      setFavorite((prev) => !prev);
    },
  });

  useEffect(() => {
    if (product?.item?._id) {
      const isFavorite = checkFavoriteByProductId(product?.item?._id);
      setFavorite(isFavorite);
    }
  }, [product?.item?._id]);

  useEffect(() => {
    const existentItem = items.get(product?.item?._id);
    if (existentItem) {
      setVariables({
        instruction: existentItem.instrucao,
        quality: existentItem.qualidade,
        quantity: existentItem.quantidade,
        total: existentItem.preco_total,
      });
    } else {
      if (variables?.quantity !== 0) {
        setVariables(initialVariables);
      }
    }
  }, [items]);

  const onPressIncrease = () => {
    increaseItem(product, 1, () => {
      if (variables.quantity === 0) {
        const { quality, instruction } = variables;
        if (instruction) {
          changeItemInstruction(productId, instruction);
        }
        if (quality) {
          changeItemQuality(productId, quality);
        }
      }
    });
  };

  const onPressDecrease = useCallback(() => {
    decreaseItem(product);
  }, [product]);

  const onPressAddCustom = useCallback(
    (quantity: number) => {
      increaseItem(product, quantity);
    },
    [product]
  );

  const onChangeInstruction = (instruction: string) => {
    if (variables.quantity > 0) {
      changeItemInstruction(productId, instruction);
    }
    setVariables((prev) => ({ ...prev, instruction }));
  };

  const onChangeQuality = (quality: OrderItemQualityEnum) => {
    if (variables.quantity > 0) {
      changeItemQuality(productId, quality);
    }
    setVariables((prev) => ({ ...prev, quality }));
  };

  const onPressFavorite = () => {
    if (isFavorite) {
      removeFavorite(productId);
    } else {
      addFavorite(productId);
    }
  };

  const Screen = useMemo(() => (isWebMode ? ProductWeb : ProductMobile), [isWebMode]);

  return (
    <Screen
      product={product}
      variables={variables}
      isLoading={isLoading}
      isFavotire={isFavorite}
      onChangeInstruction={onChangeInstruction}
      onChangeQuality={onChangeQuality}
      onPressDecrease={onPressDecrease}
      onPressIncrease={onPressIncrease}
      onPressAddCustom={onPressAddCustom}
      onPressFavorite={onPressFavorite}
    />
  );
};

export default Product;
